import React, { useState } from 'react';

import GroupWorkIcon from '@mui/icons-material/GroupWork';
import InfoIcon from '@mui/icons-material/Info';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Button,
  SwipeableDrawer,
  useTheme,
  AppBar,
  Box,
  IconButton,
  Tab,
  Tabs,
  Toolbar,
} from '@mui/material';
import { isMobile } from 'react-device-detect';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';

import EnemySwitcher from './Common/EnemySwitcher';
import ServerSelector from './Common/ServerSelector';
import { useAppState } from '../context/AppState';
import { useIntelState } from '../context/IntelState';
import { signOut } from '../services/Auth';
import { ReactComponent as DarkModeSVG } from '../svgs/design/nav-dark-mode-svg-1.svg';
import { ReactComponent as EnemySVG } from '../svgs/design/nav-enemy-svg-1.svg';
import { ReactComponent as KingdomSVG } from '../svgs/design/nav-kingdom-svg-1.svg';
import { ReactComponent as LightModeSVG } from '../svgs/design/nav-light-mode-svg-1.svg';
import { ReactComponent as LogoutSVG } from '../svgs/design/nav-logout-svg-1.svg';
import { ReactComponent as NewsSVG } from '../svgs/design/nav-news-svg-1.svg';
import { ReactComponent as OpsSVG } from '../svgs/design/nav-ops-svg-1.svg';
import { ReactComponent as RecentSVG } from '../svgs/design/nav-recent-svg-1.svg';
import { ReactComponent as SimSVG } from '../svgs/design/nav-simulation-svg-0.svg';
import { ReactComponent as StatsSVG } from '../svgs/design/nav-throne-svg-1.svg';
import { ReactComponent as WarSVG } from '../svgs/design/nav-war-svg-1.svg';

interface MenuProps {
  onClick: () => void;
  text: string;
}

const MenuButton: React.FC<MenuProps> = ({ onClick, text }) => {
  return (
    <Button
      variant='outlined'
      onClick={onClick}
      size='small'
      sx={{ marginBottom: 1 }}
      fullWidth
    >
      {text}
    </Button>
  );
};

const pages = [
  { text: 'Kingdom', url: '/', isDevOnly: false },
  { text: 'Enemy', url: '/enemy', isDevOnly: false },
  { text: 'Recent', url: '/recent', isDevOnly: false },
  { text: 'KD Stats', url: '/stats', isDevOnly: false },
  { text: 'OPs', url: '/opsplanner', isDevOnly: false },
  { text: 'Users', url: '/users', isDevOnly: false },
  { text: 'News', url: '/kingdomnews', isDevOnly: false },
  // { text: 'Sim', url: '/simulation', isDevOnly: false },
];

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { appState, appDispatch } = useAppState();
  const { intelDispatch } = useIntelState();
  const [sideOpen, setSideOpen] = useState(false);
  const svgStyle = {
    height: '30px',
    width: '30px',
    fill: theme.palette.text.secondary,
  };
  const lightModeSvg = appState.darkMode ? (
    <DarkModeSVG style={svgStyle} />
  ) : (
    <LightModeSVG style={svgStyle} />
  );

  const getPageIndex = () => {
    const path = location.pathname.toLowerCase();
    return Math.max(
      pages.findIndex((page) => page.url === path),
      0
    );
  };
  const pageTabIndex = getPageIndex();

  const toggleSideMenu =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event &&
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }

      setSideOpen(open);
    };

  const getIcon = (value: string, isSelected: boolean) => {
    const iconStyle = {
      height: '20px',
      width: '20px',
      fill: isSelected
        ? theme.palette.primary.main
        : theme.palette.text.secondary,
    };
    switch (value) {
      case 'Kingdom':
        return <KingdomSVG style={iconStyle} />;
      case 'Enemy':
        return <EnemySVG style={iconStyle} />;
      case 'KD Stats':
        return <StatsSVG style={iconStyle} />;
      case 'OPs':
        return <WarSVG style={iconStyle} />;
      case 'Users':
        return <GroupWorkIcon style={iconStyle} />;
      case 'News':
        return <NewsSVG style={iconStyle} />;
      case 'Recent Ops':
        return <OpsSVG style={iconStyle} />;
      case 'Recent':
        return <RecentSVG style={iconStyle} />;
      case 'Sim':
        return <SimSVG style={iconStyle} />;
    }
  };

  const handleTabChange = (newValue: number) => {
    navigate(pages[newValue].url);
  };

  const handleLogOut = () => {
    appDispatch({
      type: 'RESET_APP_STATE',
    });
    intelDispatch({
      type: 'RESET_INTEL_STATE',
    });
    signOut();
    navigate('login');
  };

  const renderLogo = () => {
    return (
      <Box sx={{ padding: isMobile ? 0 : 1 }}>
        <img
          src={'/utopia_logo_blue - 192x192.png'}
          alt='Utopia Intel Site'
          loading='lazy'
          style={{ height: isMobile ? '30px' : '40px' }}
        />
      </Box>
    );
  };

  const renderMobileToolbar = () => {
    return (
      <Box
        sx={{
          flexGrow: 1,
          display: { xs: 'flex', md: 'none' },
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingRight: 1,
        }}
      >
        <Box>
          <Button
            onClick={() => {
              handleTabChange(0);
            }}
            sx={{ mx: 1 }}
          >
            Kingdom
          </Button>
          <Button
            onClick={() => {
              handleTabChange(1);
            }}
            sx={{ mx: 1 }}
          >
            Enemy
          </Button>
          <Button
            onClick={() => {
              handleTabChange(2);
            }}
            sx={{ mx: 1 }}
          >
            Recent
          </Button>
        </Box>
        <IconButton
          size='large'
          onClick={toggleSideMenu(true)}
          color='inherit'
          sx={{
            padding: 1,
          }}
        >
          <MenuIcon />
        </IconButton>
      </Box>
    );
  };

  const renderDesktopTabs = () => {
    return (
      <Box
        sx={{
          flexGrow: 1,
          display: { xs: 'none', md: 'flex' },
          marginLeft: 1,
          alignItems: 'center',
          justifyContent: 'start',
        }}
      >
        <Tabs
          value={pageTabIndex}
          onChange={(event: React.SyntheticEvent, newValue: number) =>
            handleTabChange(newValue)
          }
          aria-label='main navigation'
        >
          {pages.map((page, index) => (
            <Tab
              key={page.text}
              label={page.text}
              icon={getIcon(page.text, index === pageTabIndex)}
            />
          ))}
        </Tabs>
        <Box sx={{ marginX: 1 }}>
          <ServerSelector />
        </Box>
      </Box>
    );
  };

  const renderDesktopIcons = () => {
    return (
      <Box
        sx={{
          flexGrow: 0,
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
        }}
      >
        <IconButton
          aria-label='display mode'
          onClick={() => navigate('credits')}
          size='large'
        >
          {<InfoIcon />}
        </IconButton>
        <IconButton
          aria-label='display mode'
          onClick={() =>
            appDispatch({
              type: 'SET_DARK_MODE',
              payload: !appState.darkMode,
            })
          }
          size='large'
        >
          {lightModeSvg}
        </IconButton>
        <IconButton aria-label='logout' onClick={handleLogOut} size='large'>
          <LogoutSVG style={svgStyle} />
        </IconButton>
      </Box>
    );
  };

  return (
    <div className='Layout'>
      <AppBar
        position={isMobile ? 'fixed' : 'static'}
        style={{
          background: appState.darkMode ? '#121212' : '#DDD',
        }}
        sx={isMobile ? { bottom: 'auto', top: 0 } : {}}
      >
        <Toolbar
          disableGutters
          style={{ height: isMobile ? '40px' : '56px', minHeight: 'unset' }}
        >
          {renderLogo()}
          {renderDesktopTabs()}
          {renderDesktopIcons()}
          {renderMobileToolbar()}
        </Toolbar>
      </AppBar>

      <main style={{ paddingTop: isMobile ? '40px' : 0 }}>
        <Outlet />
      </main>
      <SwipeableDrawer
        anchor={'left'}
        open={sideOpen}
        onClose={toggleSideMenu(false)}
        onOpen={toggleSideMenu(true)}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 1,
          }}
        >
          <Box sx={{ marginY: 1 }}>
            <EnemySwitcher />
          </Box>

          {pages.slice(3).map((page, i) => (
            <MenuButton
              key={page.url}
              onClick={() => {
                handleTabChange(i + 3);
              }}
              text={page.text}
            />
          ))}

          <MenuButton
            key='credits'
            onClick={() => {
              navigate('credits');
            }}
            text='Credits'
          />
          <MenuButton
            key='mode'
            onClick={() => {
              appDispatch({
                type: 'SET_DARK_MODE',
                payload: !appState.darkMode,
              });
            }}
            text='Light Mode'
          />
          <MenuButton key='logout' onClick={handleLogOut} text='Logout' />
          <Box sx={{ marginY: 1 }}>
            <ServerSelector />
          </Box>
        </Box>
      </SwipeableDrawer>
    </div>
  );
};

export default Layout;
